import { mockApiCall, API_BASE_URL } from './index';
import { getToken, getUser } from './auth';

const parseMensajeJson = (raw) => {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
};

const deriveMessageType = (msg, parsedMensaje) => {
  if (msg.media_id) {
    const mime = msg.mime_type || '';
    if (mime.startsWith('image/')) return 'image';
    if (mime.startsWith('video/')) return 'video';
    if (mime.startsWith('audio/')) return 'audio';
    return 'document';
  }
  if (parsedMensaje.interactive && Array.isArray(parsedMensaje.interactive.options)) return 'interactive';
  if (parsedMensaje.lat != null && parsedMensaje.lng != null) return 'location';
  if (Array.isArray(parsedMensaje.contacts) && parsedMensaje.contacts.length > 0) return 'contacts';
  return 'text';
};

/**
 * Direct URL to a media BLOB. Auth goes in the query string so it works with
 * native <img>/<video>/<audio> tags. The browser then handles caching and
 * lazy loading natively, instead of forcing fetch+blob in JS for every render.
 */
export const mediaSrc = (mediaId) => {
  if (!mediaId) return null;
  const token = getToken();
  const user = getUser();
  if (!token || !user?.companyId) return null;
  return `${API_BASE_URL}/api/whatsapp/media/${encodeURIComponent(mediaId)}?token=${encodeURIComponent(token)}&company_id=${encodeURIComponent(String(user.companyId))}`;
};

const whatsappPost = async (path, body) => {
  const token = getToken();
  const user = getUser();
  if (!token) throw new Error('No authentication token');
  const res = await fetch(`${API_BASE_URL}/api/whatsapp/${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Session-Token': token,
      'X-Company-ID': user?.companyId || '',
    },
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(err.error || `Request failed (${res.status})`);
  }
  return res.json();
};

export const mapBackendMessage = (msg) => {
  const parsed = parseMensajeJson(msg.mensaje);
  const content =
    msg.content ||
    parsed.texto ||
    parsed.text ||
    (typeof msg.mensaje === 'string' && !parsed.texto ? msg.mensaje : '');

  let type = 'customer';
  if (msg.role === 'assistant') type = 'agent';
  else if (msg.role === 'system') type = 'system';

  return {
    id:
      msg.id ||
      `${msg.transmisor}-${msg.fecha}-${(msg.content || '').substring(0, 10)}`,
    type,
    messageType: deriveMessageType(msg, parsed),
    content,
    caption: msg.caption || null,
    timestamp: msg.fecha || new Date().toISOString(),
    agent: msg.role === 'assistant' ? 'Agent' : undefined,
    status: msg.status || null,
    mediaId: msg.media_id || null,
    mimeType: msg.mime_type || null,
    filename: msg.filename || parsed.filename || null,
    sizeBytes: msg.size_bytes ?? null,
    durationS: msg.duration_s ?? parsed.duration_s ?? null,
    width: msg.width ?? null,
    height: msg.height ?? null,
    voice: !!parsed.voice,
    waMessageId: msg.wa_message_id || null,
    replyToId: msg.reply_to_id || null,
    forwarded: !!msg.forwarded,
    editedAt: msg.edited_at || null,
    deletedAt: msg.deleted_at || null,
    originalContent: msg.original_content || null,
    isInternal: !!msg.is_internal,
    authorUserId: msg.author_user_id || null,
    authorName: msg.author_name || null,
    location:
      parsed.lat != null && parsed.lng != null
        ? {
            lat: parsed.lat,
            lng: parsed.lng,
            name: parsed.name || null,
            address: parsed.address || null,
          }
        : null,
    contacts: Array.isArray(parsed.contacts) ? parsed.contacts : null,
    interactive:
      parsed.interactive && Array.isArray(parsed.interactive.options)
        ? {
            type: parsed.interactive.type || 'button',
            bodyText: parsed.interactive.body_text || '',
            options: parsed.interactive.options,
          }
        : null,
  };
};

// Mock conversations data
let mockConversations = [
  {
    id: 1,
    customerName: 'Maria Garcia',
    customerId: 'CUST001',
    phone: '+1234567890',
    status: 'open',
    assignedTo: 'John Doe',
    assignedToId: 1,
    lastMessage: 'I need help with my account',
    lastMessageTime: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
    tags: ['billing', 'urgent'],
    unreadCount: 2,
    messages: [
      {
        id: 1,
        type: 'customer',
        content: 'Hello, I need help with my account',
        timestamp: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
      },
      {
        id: 2,
        type: 'agent',
        content: 'Hi Maria, I can help you with that. What seems to be the issue?',
        timestamp: new Date(Date.now() - 25 * 60 * 1000).toISOString(),
        agent: 'John Doe',
      },
      {
        id: 3,
        type: 'customer',
        content: 'I need help with my account',
        timestamp: new Date(Date.now() - 5 * 60 * 1000).toISOString(),
      },
    ],
  },
  {
    id: 2,
    customerName: 'Carlos Rodriguez',
    customerId: 'CUST002',
    phone: '+1234567891',
    status: 'in_progress',
    assignedTo: 'Jane Smith',
    assignedToId: 2,
    lastMessage: 'Thank you for your help!',
    lastMessageTime: new Date(Date.now() - 15 * 60 * 1000).toISOString(),
    tags: ['support'],
    unreadCount: 0,
    messages: [
      {
        id: 1,
        type: 'customer',
        content: 'I have a question about payments',
        timestamp: new Date(Date.now() - 60 * 60 * 1000).toISOString(),
      },
      {
        id: 2,
        type: 'agent',
        content: 'Sure, I can help with that. What would you like to know?',
        timestamp: new Date(Date.now() - 55 * 60 * 1000).toISOString(),
        agent: 'Jane Smith',
      },
      {
        id: 3,
        type: 'customer',
        content: 'Thank you for your help!',
        timestamp: new Date(Date.now() - 15 * 60 * 1000).toISOString(),
      },
    ],
  },
  {
    id: 3,
    customerName: 'Ana Martinez',
    customerId: 'CUST003',
    phone: '+1234567892',
    status: 'resolved',
    assignedTo: null,
    assignedToId: null,
    lastMessage: 'Issue resolved',
    lastMessageTime: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    tags: ['technical'],
    unreadCount: 0,
    messages: [
      {
        id: 1,
        type: 'customer',
        content: 'I cannot log in to my account',
        timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
      },
      {
        id: 2,
        type: 'agent',
        content: 'Let me help you reset your password',
        timestamp: new Date(Date.now() - 2.5 * 60 * 60 * 1000).toISOString(),
        agent: 'John Doe',
      },
      {
        id: 3,
        type: 'system',
        content: 'Issue resolved',
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      },
    ],
  },
];

export const conversationsApi = {
  getConversations: async (filters = {}) => {
    const token = getToken();
    const user = getUser();
    
    if (!token) {
      throw new Error('No authentication token');
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/whatsapp/getchats`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Token': token,
          'X-Company-ID': user?.companyId || '',
        },
        body: JSON.stringify({
          only_archived: !!filters.onlyArchived,
          include_archived: !!filters.includeArchived,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to load conversations' }));
        throw new Error(error.error || 'Failed to load conversations');
      }

      const result = await response.json();
      
      // Backend returns { conversations: [...], data: [...], total: ... }
      let conversations = result.conversations || result.data || [];
      
      // Apply client-side filtering (backend doesn't support filters yet)
      if (filters.status) {
        conversations = conversations.filter((c) => c.status === filters.status);
      }

      if (filters.assigned === 'assigned') {
        conversations = conversations.filter((c) => c.assignedTo !== null);
      } else if (filters.assigned === 'unassigned') {
        conversations = conversations.filter((c) => c.assignedTo === null);
      }

      if (filters.tags && filters.tags.length > 0) {
        conversations = conversations.filter((c) =>
          filters.tags.some((tag) => (c.tags || []).includes(tag))
        );
      }

      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        conversations = conversations.filter(
          (c) =>
            c.customerName?.toLowerCase().includes(searchLower) ||
            c.customerId?.toLowerCase().includes(searchLower) ||
            c.phone?.includes(searchLower)
        );
      }

      return {
        data: conversations,
        total: conversations.length,
        page: filters.page || 1,
        pageSize: filters.pageSize || 50,
      };
    } catch (error) {
      console.error('Error fetching conversations:', error);
      throw error;
    }
  },

  getConversation: async (id) => {
    const token = getToken();
    const user = getUser();
    
    if (!token) {
      throw new Error('No authentication token');
    }

    try {
      // Get the conversation details from the conversations list
      const conversationsResponse = await fetch(`${API_BASE_URL}/api/whatsapp/getchats`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Token': token,
          'X-Company-ID': user?.companyId || '',
        },
        body: JSON.stringify({}),
      });

      if (!conversationsResponse.ok) {
        throw new Error('Failed to load conversations');
      }

      const conversationsResult = await conversationsResponse.json();
      const conversations = conversationsResult.conversations || conversationsResult.data || [];
      const conversation = conversations.find((c) => c.id === id || c.phone === id);
      
      if (!conversation) {
        throw new Error('Conversation not found');
      }

      // Get WhatsApp number from company settings (needed for getmessages endpoint)
      // We'll call getmessages endpoint to get the messages
      const phoneNumber = conversation.phone || conversation.id; // Conversation phone number
      
      // Load initial messages page
      let messages = [];
      let hasMore = false;
      let nextBefore = null;
      try {
        const messagesResponse = await fetch(`${API_BASE_URL}/api/whatsapp/getmessages`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Session-Token': token,
            'X-Company-ID': user?.companyId || '',
          },
          body: JSON.stringify({
            sender: phoneNumber, // Conversation phone number
            // receiver is optional; backend will resolve company WhatsApp from X-Company-ID
            limit: 50,
          }),
        });

        if (messagesResponse.ok) {
          const messagesResult = await messagesResponse.json();
          const backendMessages = messagesResult.messages || [];
          hasMore = !!messagesResult.has_more;
          nextBefore = messagesResult.next_before || null;
          
          messages = backendMessages
            .map(mapBackendMessage)
            .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        }
      } catch (error) {
        console.error('Error loading messages:', error);
        // Continue with empty messages array if there's an error
      }

      return {
        ...conversation,
        messages: messages,
        hasMoreMessages: hasMore,
        nextBefore: nextBefore,
        customerId: conversation.id || conversation.phone,
        tags: conversation.tags || [],
        status: conversation.status || 'open',
        assignedTo: conversation.assignedTo || null,
        assignedToId: conversation.assignedToId || null,
      };
    } catch (error) {
      console.error('Error fetching conversation:', error);
      throw error;
    }
  },

  getMessages: async (conversationId, options = {}) => {
    const token = getToken();
    const user = getUser();
    if (!token) throw new Error('No authentication token');
    if (!conversationId) throw new Error('Conversation ID is required');

    const { before = null, limit = 50 } = options;

    const response = await fetch(`${API_BASE_URL}/api/whatsapp/getmessages`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Session-Token': token,
        'X-Company-ID': user?.companyId || '',
      },
      body: JSON.stringify({
        sender: conversationId,
        before,
        limit,
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to load messages' }));
      throw new Error(error.error || 'Failed to load messages');
    }

    const result = await response.json();
    const backendMessages = result.messages || [];

    const mapped = backendMessages
      .map(mapBackendMessage)
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

    return {
      messages: mapped,
      hasMore: !!result.has_more,
      nextBefore: result.next_before || null,
      limit: result.limit || limit,
    };
  },

  sendMessage: async (conversationId, opts) => {
    const token = getToken();
    const user = getUser();

    if (!token) throw new Error('No authentication token');
    if (!conversationId) throw new Error('Conversation ID is required');

    // Backwards compat: string == plain text
    const payload = typeof opts === 'string' ? { text: opts } : (opts || {});
    const text = (payload.text || '').trim();
    const mediaId = payload.mediaId || null;

    if (!text && !mediaId) {
      throw new Error('Message cannot be empty');
    }

    const body = {
      receiver: conversationId,
      sender: user?.whatsapp || '',
    };
    if (mediaId) {
      body.media_id = mediaId;
      if (payload.type) body.type = payload.type;
      if (payload.caption) body.caption = payload.caption;
      if (payload.filename) body.filename = payload.filename;
      if (payload.voice) body.voice = true;
    } else {
      body.message = text;
    }
    if (payload.replyToId) body.reply_to_id = payload.replyToId;
    if (payload.internal) body.internal = true;

    const response = await fetch(`${API_BASE_URL}/api/whatsapp/sendmessage`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Session-Token': token,
        'X-Company-ID': user?.companyId || '',
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to send message' }));
      throw new Error(error.error || 'Failed to send message');
    }

    const result = await response.json();
    return {
      id: result.wa_message_id || Date.now().toString(),
      type: 'agent',
      content: text || payload.caption || '',
      timestamp: new Date().toISOString(),
      agent: 'Current User',
      mediaId: result.media_id || mediaId || null,
      waMessageId: result.wa_message_id || null,
    };
  },

  uploadMedia: async (file, type) => {
    const token = getToken();
    const user = getUser();
    if (!token) throw new Error('No authentication token');
    if (!file) throw new Error('File is required');

    const form = new FormData();
    form.append('file', file, file.name || 'archivo');
    if (type) form.append('type', type);

    const response = await fetch(`${API_BASE_URL}/api/whatsapp/upload-media`, {
      method: 'POST',
      headers: {
        'X-Session-Token': token,
        'X-Company-ID': user?.companyId || '',
      },
      body: form,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(error.error || 'Upload failed');
    }
    const r = await response.json();
    return {
      mediaId: r.media_id,
      mimeType: r.mime_type,
      filename: r.filename,
      sizeBytes: r.size_bytes,
      mediaClass: r.media_class,
      voice: !!r.voice,
      durationS: r.duration_s ?? null,
    };
  },

  editMessage: async (messageId, newText) => {
    return whatsappPost('editmessage', { message_id: messageId, new_text: newText });
  },

  hideMessage: async (messageId) => {
    return whatsappPost('hide-message', { message_id: messageId });
  },

  forwardMessage: async (messageId, receivers) => {
    return whatsappPost('forwardmessage', { message_id: messageId, receivers });
  },

  listOperators: async () => {
    const token = getToken();
    const user = getUser();
    if (!token) throw new Error('No authentication token');
    const res = await fetch(`${API_BASE_URL}/api/whatsapp/operators`, {
      headers: {
        'X-Session-Token': token,
        'X-Company-ID': user?.companyId || '',
      },
    });
    if (!res.ok) throw new Error('Failed to load operators');
    const r = await res.json();
    return r.operators || [];
  },

  updateConversation: async (id, updates) => {
    const token = getToken();
    const user = getUser();
    
    if (!token) {
      throw new Error('No authentication token');
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/whatsapp/updateconversation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Token': token,
          'X-Company-ID': user?.companyId || '',
        },
        body: JSON.stringify({
          conversation_id: id,
          id: id,  // Also send as 'id' for compatibility
          ...updates,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to update conversation' }));
        throw new Error(error.error || 'Failed to update conversation');
      }

      const result = await response.json();
      
      // Map backend response to frontend format
      return {
        ...result,
        customerId: result.id || result.phone,
        tags: updates.tags || [],
      };
    } catch (error) {
      console.error('Error updating conversation:', error);
      throw error;
    }
  },

  getAISuggestions: async (conversationId) => {
    // TODO: Replace with real API call
    // const response = await fetch(`${API_BASE_URL}/conversations/${conversationId}/ai-suggestions`, {
    //   headers: { Authorization: `Bearer ${getToken()}` },
    // });
    // return response.json();

    const suggestions = [
      'Thank you for contacting us. How can I assist you today?',
      'I understand your concern. Let me help you with that.',
      'I apologize for the inconvenience. Let me resolve this for you.',
    ];

    return mockApiCall(suggestions, 'Failed to get AI suggestions', 1000);
  },
};

